<template>
    <AppLayout v-bind="appSharedData.getAppSidebarLayoutProps(pageMeta)">
        <template #right>
            <Button
                as="a"
                target="_blank"
                color="light"
                iconName="external-link"
                iconClass="w-3.5 h-3.5"
                :href="viewServiceUrl"
            >
                View
            </Button>
        </template>

        <div>
            <ServiceGeneralSettingsSection
                :serviceAggregate="localServiceAggregate"
                :meetingLocations="meetingLocations"
                :paymentIntegrations="localPaymentIntegrations"
            />
            <SideFormSectionDivider />
            <ServiceSchedulingSettingsSection
                :serviceAggregate="localServiceAggregate"
            />
            <SideFormSectionDivider />
            <ServiceAttendeesSettingsSection
                :service="localServiceAggregate.service"
            />
            <SideFormSectionDivider />
            <ServiceGroupSchedulingSettingsSection
                :service="localServiceAggregate.service"
                :isDisabled="
                    !localServiceAggregate.service.supportsMultipleAttendeesPerMeeting()
                "
            />
            <SideFormSectionDivider />
            <ServiceRecurrenceSettingsSection
                :service="localServiceAggregate.service"
                :isDisabled="
                    localServiceAggregate.service.doesNotAllowsRecurrence()
                "
            />
            <SideFormSectionDivider />
            <ServiceCompanyUsersSection
                :companyUserAggregates="localCompanyUserAggregate"
                :serviceAggregate="localServiceAggregate"
            />
            <SideFormSectionDivider />
            <ServicePaymentSettingsSection
                :serviceAggregate="localServiceAggregate"
                :paymentIntegrations="localPaymentIntegrations"
            />
            <SideFormSectionDivider />
            <ServiceCustomFieldSection
                :serviceAggregate="localServiceAggregate"
            />
            <SideFormSectionDivider />
            <GenericAvailabilitySection
                :entityWithAssociatedAvailability="localServiceAggregate"
                :availabilities="localAvailabilities"
                :associateAvailabilityUrl="
                    urlFactory.makeServiceAssociateAvailabilityUrl(
                        localServiceAggregate.service.id,
                    )
                "
            />
        </div>

        <PageBottomPadding />
    </AppLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import AppLayout from '@/Shared/Layout/App/Sidebar/AppLayout.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ServiceGeneralSettingsSection from '@/App/Service/Component/Service/ServiceGeneralSettingsSection.vue'
    import ServiceCompanyUsersSection from '@/App/Service/Component/CompanyUser/ServiceCompanyUsersSection.vue'
    import ServicePaymentSettingsSection from '@/App/Service/Component/ServicePrice/ServicePaymentSettingsSection.vue'
    import ServiceCustomFieldSection from '@/App/Service/Component/CustomField/ServiceCustomFieldSection.vue'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import Integration from '@/App/Integration/Entity/Integration'
    import Availability from '@/App/Availability/Entity/Availability'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import SideFormSectionDivider from '@/Shared/Component/SideFormSectionDivider.vue'
    import ServiceSchedulingSettingsSection from '@/App/Service/Component/Scheduling/ServiceSchedulingSettingsSection.vue'
    import {
        type MeetingLocationType,
        type PageMeta,
    } from '@/Shared/AutoGenerated/Main'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import PageBottomPadding from '@/Shared/Layout/App/Sidebar/PageBottomPadding.vue'
    import ServiceAttendeesSettingsSection from '@/App/Service/Component/Service/ServiceAttendeesSettingsSection.vue'
    import GenericAvailabilitySection from '@/App/Availability/Component/GenericAvailabilitySection.vue'
    import ServiceRecurrenceSettingsSection from '@/App/Service/Component/Service/ServiceRecurrenceSettingsSection.vue'
    import ServiceGroupSchedulingSettingsSection from '@/App/Service/Component/Service/ServiceGroupSchedulingSettingsSection.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface EditServiceProps {
        pageMeta: PageMeta
        appSharedDataData: any
        serviceAggregateData: any
        paymentIntegrationsData: any[]
        companyUserAggregatesData: any[]
        meetingLocations: MeetingLocationType[]
        availabilitiesData: any
    }

    const props = defineProps<EditServiceProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const authCompany = appSharedData.getAuthCompany()
    const localServiceAggregate = ref<ServiceAggregate>(
        ServiceAggregate.hydrateSingle(props.serviceAggregateData),
    )
    const localPaymentIntegrations = ref<Integration[]>(
        Integration.hydrateMultiple(props.paymentIntegrationsData),
    )
    const localCompanyUserAggregate = ref<CompanyUserAggregate[]>(
        CompanyUserAggregate.hydrateMultiple(props.companyUserAggregatesData),
    )
    const localAvailabilities = ref<Availability[]>(
        Availability.hydrateMultiple(props.availabilitiesData),
    )
    const urlFactory = new UrlFactory()
    const viewServiceUrl = urlFactory.makeServiceBookingFormPageUrl(
        authCompany.slug,
        localServiceAggregate.value.service.slug,
    )
</script>
